import { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, ArrowDownLeft, ArrowUpRight, Eye, X } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { agencies } from '../../data/agencies';
import { GET_RECEIVED_BOOKINGS, GET_SENT_BOOKINGS } from '../../graphql/queries/booking';
import { GET_AGENCY } from '../../graphql/queries/agency';
import { ACCEPT_BOOKING, REJECT_BOOKING, DELETE_BOOKING, REQUEST_MODIFICATIONS } from '../../graphql/mutations/booking';
import { useMutation } from '@apollo/client';

const STATUS_OPTIONS = [
  { value: 'pending_availability', label: 'En attente de confirmation de la disponibilité' },
  { value: 'accepted_unpaid', label: 'Réservation acceptée - non payée' },
  { value: 'deposit_paid', label: 'Acompte payé' },
  { value: 'fully_paid', label: 'Montant total payé' },
  { value: 'paid_partner', label: 'Paiement effectué via un partenaire' },
  { value: 'paid_organizer', label: "Paiement encaissé par l'organisateur" },
  { value: 'cancelled', label: 'Annulée' },
];
const PAIEMENT_OPTIONS = [
  { value: 'paye', label: 'Payé' },
  { value: 'avance', label: 'Avance' },
  { value: 'non_paye', label: 'Pas payé' },
];
const TYPE_RESERVATION_OPTIONS = [
  { value: 'enligne', label: 'En ligne' },
  { value: 'agence', label: 'Agence' },
  { value: 'partenaire', label: 'Partenaire' },
];

const mockBookings = [
  // En attente de confirmation - agence destinataire = moi, émettrice = partenaire
  {
    id: '101',
    packageId: 'med-cruise-wa',
    packageTitle: 'Biskra Hammam et Ghoufi',
    agencyId: 1, // moi (destinataire)
    fromAgencyId: 'globetrotter', // partenaire (émettrice)
    userId: 'userA',
    status: 'pending_availability',
    statutDisponibilite: 'attente',
    paiement: 'non_paye',
    montantAvance: 0,
    typeReservation: 'agence',
    travelerCount: 2,
    totalPrice: 70000,
    commissions: { platform: 3500, agency: 7000 },
    contactInfo: { firstName: 'Ali', lastName: 'Benali', email: 'ali@example.com', phone: '0600000001' },
    createdAt: '2024-03-01T10:00:00Z',
    updatedAt: '2024-03-01T10:30:00Z',
  },
  // En attente de confirmation - agence destinataire = autre, émettrice = moi
  {
    id: '102',
    packageId: 'omra-1001',
    packageTitle: 'Omra Ramadan 2024',
    agencyId: 'globetrotter', // destinataire
    fromAgencyId: 1, // moi (émettrice)
    userId: 'userB',
    status: 'pending_availability',
    statutDisponibilite: 'attente',
    paiement: 'non_paye',
    montantAvance: 0,
    typeReservation: 'partenaire',
    travelerCount: 1,
    totalPrice: 250000,
    commissions: { platform: 12500, agency: 25000 },
    contactInfo: { firstName: 'Sami', lastName: 'Khelifa', email: 'sami@example.com', phone: '0600000002' },
    createdAt: '2024-03-02T10:00:00Z',
    updatedAt: '2024-03-02T10:30:00Z',
  },
  // Mes réservations (émise et reçue par moi)
  {
    id: '103',
    packageId: 'med-cruise-wa',
    packageTitle: 'Biskra Hammam et Ghoufi',
    agencyId: 1,
    fromAgencyId: 1,
    userId: 'userC',
    status: 'accepted_unpaid',
    statutDisponibilite: 'disponible',
    paiement: 'non_paye',
    montantAvance: 0,
    typeReservation: 'agence',
    travelerCount: 3,
    totalPrice: 90000,
    commissions: { platform: 4000, agency: 8000 },
    contactInfo: { firstName: 'Leila', lastName: 'Bensalem', email: 'leila@example.com', phone: '0600000003' },
    createdAt: '2024-03-03T10:00:00Z',
    updatedAt: '2024-03-03T10:30:00Z',
  },
  {
    id: '104',
    packageId: 'omra-1001',
    packageTitle: 'Omra Ramadan 2024',
    agencyId: 'globetrotter',
    fromAgencyId: 1,
    userId: 'userD',
    status: 'accepted_unpaid',
    statutDisponibilite: 'disponible',
    paiement: 'non_paye',
    montantAvance: 0,
    typeReservation: 'enligne',
    travelerCount: 2,
    totalPrice: 500000,
    commissions: { platform: 20000, agency: 40000 },
    contactInfo: { firstName: 'Nora', lastName: 'Ait', email: 'nora@example.com', phone: '0600000004' },
    createdAt: '2024-03-04T10:00:00Z',
    updatedAt: '2024-03-04T10:30:00Z',
  },

  // Acompte payé
  {
    id: '105',
    packageId: 'med-cruise-wa',
    packageTitle: 'Biskra Hammam et Ghoufi',
    agencyId: 1,
    fromAgencyId: 1,
    userId: 'userE',
    status: 'deposit_paid',
    statutDisponibilite: 'disponible',
    paiement: 'avance',
    montantAvance: 30000,
    typeReservation: 'agence',
    travelerCount: 2,
    totalPrice: 70000,
    commissions: { platform: 3500, agency: 7000 },
    contactInfo: { firstName: 'Karim', lastName: 'Ziani', email: 'karim@example.com', phone: '0600000005' },
    createdAt: '2024-03-05T10:00:00Z',
    updatedAt: '2024-03-05T10:30:00Z',
  },
  {
    id: '106',
    packageId: 'omra-1001',
    packageTitle: 'Omra Ramadan 2024',
    agencyId: 'globetrotter',
    fromAgencyId: 1,
    userId: 'userF',
    status: 'deposit_paid',
    statutDisponibilite: 'disponible',
    paiement: 'avance',
    montantAvance: 50000,
    typeReservation: 'partenaire',
    travelerCount: 1,
    totalPrice: 250000,
    commissions: { platform: 12500, agency: 25000 },
    contactInfo: { firstName: 'Yasmine', lastName: 'Boukhelifa', email: 'yasmine@example.com', phone: '0600000006' },
    createdAt: '2024-03-06T10:00:00Z',
    updatedAt: '2024-03-06T10:30:00Z',
  },

  // Montant total payé
  {
    id: '107',
    packageId: 'med-cruise-wa',
    packageTitle: 'Biskra Hammam et Ghoufi',
    agencyId: 1,
    fromAgencyId: 1,
    userId: 'userG',
    status: 'fully_paid',
    statutDisponibilite: 'disponible',
    paiement: 'paye',
    montantAvance: 0,
    typeReservation: 'enligne',
    travelerCount: 2,
    totalPrice: 70000,
    commissions: { platform: 3500, agency: 7000 },
    contactInfo: { firstName: 'Samir', lastName: 'Belaid', email: 'samir@example.com', phone: '0600000007' },
    createdAt: '2024-03-07T10:00:00Z',
    updatedAt: '2024-03-07T10:30:00Z',
  },
  {
    id: '108',
    packageId: 'omra-1001',
    packageTitle: 'Omra Ramadan 2024',
    agencyId: 'globetrotter',
    fromAgencyId: 1,
    userId: 'userH',
    status: 'fully_paid',
    statutDisponibilite: 'disponible',
    paiement: 'paye',
    montantAvance: 0,
    typeReservation: 'agence',
    travelerCount: 1,
    totalPrice: 250000,
    commissions: { platform: 12500, agency: 25000 },
    contactInfo: { firstName: 'Imene', lastName: 'Boudiaf', email: 'imene@example.com', phone: '0600000008' },
    createdAt: '2024-03-08T10:00:00Z',
    updatedAt: '2024-03-08T10:30:00Z',
  },

  // Paiement via partenaire
  {
    id: '109',
    packageId: 'med-cruise-wa',
    packageTitle: 'Biskra Hammam et Ghoufi',
    agencyId: 1,
    fromAgencyId: 1,
    userId: 'userI',
    status: 'paid_partner',
    statutDisponibilite: 'disponible',
    paiement: 'paye',
    montantAvance: 0,
    typeReservation: 'partenaire',
    travelerCount: 2,
    totalPrice: 70000,
    commissions: { platform: 3500, agency: 7000 },
    contactInfo: { firstName: 'Meriem', lastName: 'Brahimi', email: 'meriem@example.com', phone: '0600000009' },
    createdAt: '2024-03-09T10:00:00Z',
    updatedAt: '2024-03-09T10:30:00Z',
  },
  {
    id: '110',
    packageId: 'omra-1001',
    packageTitle: 'Omra Ramadan 2024',
    agencyId: 'globetrotter',
    fromAgencyId: 1,
    userId: 'userJ',
    status: 'paid_partner',
    statutDisponibilite: 'disponible',
    paiement: 'paye',
    montantAvance: 0,
    typeReservation: 'partenaire',
    travelerCount: 1,
    totalPrice: 250000,
    commissions: { platform: 12500, agency: 25000 },
    contactInfo: { firstName: 'Walid', lastName: 'Bensalem', email: 'walid@example.com', phone: '0600000010' },
    createdAt: '2024-03-10T10:00:00Z',
    updatedAt: '2024-03-10T10:30:00Z',
  },

  // Paiement encaissé par l’organisateur
  {
    id: '111',
    packageId: 'med-cruise-wa',
    packageTitle: 'Biskra Hammam et Ghoufi',
    agencyId: 1,
    fromAgencyId: 1,
    userId: 'userK',
    status: 'paid_organizer',
    statutDisponibilite: 'disponible',
    paiement: 'paye',
    montantAvance: 0,
    typeReservation: 'agence',
    travelerCount: 2,
    totalPrice: 70000,
    commissions: { platform: 3500, agency: 7000 },
    contactInfo: { firstName: 'Hakim', lastName: 'Bouzid', email: 'hakim@example.com', phone: '0600000011' },
    createdAt: '2024-03-11T10:00:00Z',
    updatedAt: '2024-03-11T10:30:00Z',
  },
  {
    id: '112',
    packageId: 'omra-1001',
    packageTitle: 'Omra Ramadan 2024',
    agencyId: 'globetrotter',
    fromAgencyId: 1,
    userId: 'userL',
    status: 'paid_organizer',
    statutDisponibilite: 'disponible',
    paiement: 'paye',
    montantAvance: 0,
    typeReservation: 'enligne',
    travelerCount: 1,
    totalPrice: 250000,
    commissions: { platform: 12500, agency: 25000 },
    contactInfo: { firstName: 'Sofiane', lastName: 'Belaid', email: 'sofiane@example.com', phone: '0600000012' },
    createdAt: '2024-03-12T10:00:00Z',
    updatedAt: '2024-03-12T10:30:00Z',
  },
];


type BookingTab = 'received' | 'sent';

function BookingStatusBadge({ status }: { status: string }) {
  let color = 'gray';
  let label = status;
  switch (status) {
    case 'pending_availability':
      color = 'yellow';
      label = 'En attente de confirmation';
      break;
    case 'modification_requested':
      color = 'orange';
      label = 'Demande de modifications';
      break;
    case 'accepted_unpaid':
      color = 'orange';
      label = 'Acceptée - non payée';
      break;
    case 'deposit_paid':
      color = 'blue';
      label = 'Acompte payé';
      break;
    case 'fully_paid':
      color = 'green';
      label = 'Montant total payé';
      break;
    case 'paid_partner':
      color = 'purple';
      label = 'Payé via partenaire';
      break;
    case 'paid_organizer':
      color = 'teal';
      label = "Payé à l'organisateur";
      break;
    case 'rejected':
      color = 'red';
      label = 'Refusée';
      break;
    case 'cancelled':
      color = 'red';
      label = 'Annulée';
      break;
    default:
      color = 'gray';
      label = status;
  }
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-${color}-100 text-${color}-800`}>
      {label}
    </span>
  );
}

export default function BookingsPage() {
  const [bookingTab, setBookingTab] = useState<BookingTab>('received');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Récupérer l'ID de l'agence de l'utilisateur
  const userAgencyId = useMemo(() => {
    console.log('=== Récupération userAgencyId ===');
    console.log('user depuis useAuth:', user);
    
    // 1. Essayer depuis user.agency.id
    if (user?.agency && typeof user.agency === 'object' && 'id' in user.agency) {
      console.log('userAgencyId trouvé dans user.agency.id:', user.agency.id);
      return user.agency.id;
    }
    
    // 2. Essayer depuis user.agencyId (direct) - si disponible
    if ((user as any)?.agencyId) {
      console.log('userAgencyId trouvé dans user.agencyId:', (user as any).agencyId);
      return (user as any).agencyId;
    }
    
    // 3. Fallback: essayer de récupérer depuis localStorage
    try {
      const storedUser = localStorage.getItem('user');
      console.log('storedUser depuis localStorage:', storedUser);
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        console.log('parsed user:', parsed);
        
        // Essayer parsed.agency.id
        if (parsed.agency?.id) {
          console.log('userAgencyId trouvé dans parsed.agency.id:', parsed.agency.id);
          return parsed.agency.id;
        }
        
        // Essayer parsed.agencyId (direct) - si disponible
        if ((parsed as any).agencyId) {
          console.log('userAgencyId trouvé dans parsed.agencyId:', (parsed as any).agencyId);
          return (parsed as any).agencyId;
        }
        
        // Si on a agencyName, on peut essayer de récupérer l'ID via une query
        if (parsed.agencyName) {
          console.log('agencyName trouvé:', parsed.agencyName, '- mais pas d\'ID, il faudra faire une query');
        }
      }
    } catch (e) {
      console.error('Error parsing user from localStorage', e);
    }
    
    console.log('userAgencyId: null - Aucun ID trouvé');
    return null;
  }, [user]);

  // Si on n'a pas d'ID mais qu'on a un agencyName, récupérer l'ID via une query
  const storedUser = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, [user]);

  const agencyName = storedUser?.agencyName || user?.agencyName;
  const { data: agencyData } = useQuery(GET_AGENCY, {
    variables: { name: agencyName },
    skip: !agencyName || !!userAgencyId,
    onCompleted: (data) => {
      if (data?.agency?.id && storedUser) {
        // Mettre à jour le localStorage avec l'ID
        const updatedUser = { ...storedUser, agencyId: data.agency.id, agency: { ...storedUser.agency, id: data.agency.id } };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        console.log('ID d\'agence récupéré et sauvegardé:', data.agency.id);
      }
    },
  });

  // Utiliser l'ID de l'agence récupéré via la query si disponible
  const finalAgencyId = useMemo(() => {
    if (userAgencyId) return userAgencyId;
    if (agencyData?.agency?.id) {
      console.log('Utilisation de l\'ID d\'agence depuis la query:', agencyData.agency.id);
      return agencyData.agency.id;
    }
    return null;
  }, [userAgencyId, agencyData]);


  // Récupérer les réservations reçues (partenaires) pour l'onglet "received"
  const { data: receivedBookingsData, loading: receivedBookingsLoading, error: receivedBookingsError, refetch: refetchReceivedBookings } = useQuery(GET_RECEIVED_BOOKINGS, {
    variables: { agencyId: finalAgencyId },
    skip: !finalAgencyId || bookingTab !== 'received',
    fetchPolicy: 'cache-and-network',
  });

  // Récupérer les réservations envoyées pour l'onglet "sent"
  const { data: sentBookingsData, loading: sentBookingsLoading, error: sentBookingsError, refetch: refetchSentBookings } = useQuery(GET_SENT_BOOKINGS, {
    variables: { agencyId: finalAgencyId },
    skip: !finalAgencyId || bookingTab !== 'sent',
    fetchPolicy: 'cache-and-network',
  });

  // Mutations pour accepter/refuser/demander modifications
  const [acceptBooking] = useMutation(ACCEPT_BOOKING, {
    refetchQueries: [
      { query: GET_RECEIVED_BOOKINGS, variables: { agencyId: finalAgencyId } },
      { query: GET_SENT_BOOKINGS, variables: { agencyId: finalAgencyId } },
    ],
    onCompleted: () => {
      console.log('Réservation acceptée avec succès');
    },
  });

  const [rejectBooking] = useMutation(REJECT_BOOKING, {
    refetchQueries: [
      { query: GET_RECEIVED_BOOKINGS, variables: { agencyId: finalAgencyId } },
      { query: GET_SENT_BOOKINGS, variables: { agencyId: finalAgencyId } },
    ],
  });

  const [requestModifications] = useMutation(REQUEST_MODIFICATIONS, {
    refetchQueries: [
      { query: GET_RECEIVED_BOOKINGS, variables: { agencyId: finalAgencyId } },
      { query: GET_SENT_BOOKINGS, variables: { agencyId: finalAgencyId } },
    ],
  });

  const [deleteBooking] = useMutation(DELETE_BOOKING, {
    refetchQueries: [
      { query: GET_RECEIVED_BOOKINGS, variables: { agencyId: finalAgencyId } },
      { query: GET_SENT_BOOKINGS, variables: { agencyId: finalAgencyId } },
    ],
  });

  // Logs de débogage
  useEffect(() => {
    console.log('=== DEBUG BOOKINGS PAGE ===');
    console.log('1. userAgencyId:', userAgencyId);
    console.log('1b. finalAgencyId:', finalAgencyId);
    console.log('1c. agencyName:', agencyName);
    console.log('1d. agencyData:', agencyData);
    console.log('2. bookingTab:', bookingTab);
  }, [userAgencyId, finalAgencyId, agencyName, agencyData, bookingTab]);

  // Rafraîchir les réservations quand on revient sur la page ou quand on change d'onglet
  useEffect(() => {
    if (bookingTab === 'received' && finalAgencyId && refetchReceivedBookings) {
      console.log('Refetch des réservations "received"...');
      refetchReceivedBookings();
    }
    if (bookingTab === 'sent' && finalAgencyId && refetchSentBookings) {
      console.log('Refetch des réservations "sent"...');
      refetchSentBookings();
    }
  }, [location.pathname, bookingTab, finalAgencyId, refetchReceivedBookings, refetchSentBookings]);


  // Transformer les réservations reçues
  const receivedBookings = useMemo(() => {
    if (!receivedBookingsData?.receivedBookings) return [];
    
    return receivedBookingsData.receivedBookings.map((booking: any) => {
      // Vérifier si c'est une réservation interne (même agence)
      const packageAgencyId = booking.package?.agency?.id;
      const clientAgencyId = booking.client?.agencyId;
      const isInternalBooking = packageAgencyId && clientAgencyId && String(packageAgencyId) === String(clientAgencyId) && String(packageAgencyId) === String(finalAgencyId);
      
      const commissionPercent = booking.package?.commission || 10;
      const commissionAmount = isInternalBooking ? 0 : (booking.totalPrice * commissionPercent) / 100;
      const platformCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      const agencyCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      
      // Calculer la commission partenaire (pour les réservations de type 'partenaire' et non internes)
      const partnerCommission = (isInternalBooking || booking.typeReservation !== 'partenaire') ? 0 : commissionAmount;

      // Déterminer le statut selon le statutDisponibilite
      let status = 'pending_availability';
      
      // Vérifier d'abord si c'est une demande de modifications
      if (booking.statutReservation === 'demande_modifications' || booking.status === 'modification_requested' || booking.statutDisponibilite === 'demande_modifications') {
        status = 'modification_requested';
      } else if (booking.statutDisponibilite === 'disponible') {
        // Si acceptée, déterminer le statut selon le paiement
        if (booking.paiement === 'paye' || (booking.montantPaye && booking.montantPaye === booking.totalPrice)) {
          status = 'fully_paid';
        } else if (booking.paiement === 'avance' || (booking.montantAvance && booking.montantAvance > 0)) {
          status = 'deposit_paid';
        } else {
          status = 'accepted_unpaid';
        }
      } else if (booking.statutDisponibilite === 'non_disponible') {
        // Vérifier si c'est annulé ou refusé
        if (booking.statutReservation === 'annulée' || booking.status === 'CANCELLED') {
          status = 'cancelled';
        } else {
          status = 'rejected';
        }
      }

      return {
        id: String(booking.id),
        packageId: String(booking.packageId),
        packageTitle: booking.package?.title || '',
        agencyId: booking.package?.agency?.id || finalAgencyId,
        agencyName: booking.package?.agency?.name || '',
        fromAgencyId: booking.client?.agencyId ? String(booking.client.agencyId) : null, // L'agence qui a fait la réservation
        fromAgencyName: booking.client?.agency?.name || '', // Le nom de l'agence émettrice
        userId: String(booking.clientId),
        status: status,
        statutDisponibilite: booking.statutDisponibilite || 'attente',
        paiement: booking.paiement || 'non_paye',
        montantAvance: booking.montantAvance || 0,
        typeReservation: String(booking.typeReservation || 'partenaire'),
        travelerCount: booking.travelerCount,
        totalPrice: Number(booking.totalPrice) || 0,
        commissions: {
          platform: platformCommission,
          agency: agencyCommission,
          partner: partnerCommission,
        },
        contactInfo: {
          firstName: booking.nom || booking.client?.firstName || '',
          lastName: booking.prenom || booking.client?.lastName || '',
          email: booking.mail || booking.client?.email || '',
          phone: booking.telephone || booking.client?.phone || '',
        },
        createdAt: booking.createdAt,
        updatedAt: booking.updatedAt,
      };
    });
  }, [receivedBookingsData, finalAgencyId]);

  // Transformer les réservations envoyées
  const sentBookings = useMemo(() => {
    if (!sentBookingsData?.sentBookings) return [];
    
    return sentBookingsData.sentBookings.map((booking: any) => {
      // Vérifier si c'est une réservation interne (même agence)
      const packageAgencyId = booking.package?.agency?.id;
      const clientAgencyId = booking.client?.agencyId || finalAgencyId;
      const isInternalBooking = packageAgencyId && clientAgencyId && String(packageAgencyId) === String(clientAgencyId) && String(packageAgencyId) === String(finalAgencyId);
      
      const commissionPercent = booking.package?.commission || 10;
      const commissionAmount = isInternalBooking ? 0 : (booking.totalPrice * commissionPercent) / 100;
      const platformCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      const agencyCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      
      // Calculer la commission partenaire (pour les réservations de type 'partenaire' et non internes)
      const partnerCommission = (isInternalBooking || booking.typeReservation !== 'partenaire') ? 0 : commissionAmount;

      // Déterminer le statut selon le statutDisponibilite
      let status = 'pending_availability';
      
      // Vérifier d'abord si c'est une demande de modifications
      if (booking.statutReservation === 'demande_modifications' || booking.status === 'modification_requested' || booking.statutDisponibilite === 'demande_modifications') {
        status = 'modification_requested';
      } else if (booking.statutDisponibilite === 'disponible') {
        // Si acceptée, déterminer le statut selon le paiement
        if (booking.paiement === 'paye' || (booking.montantPaye && booking.montantPaye === booking.totalPrice)) {
          status = 'fully_paid';
        } else if (booking.paiement === 'avance' || (booking.montantAvance && booking.montantAvance > 0)) {
          status = 'deposit_paid';
        } else {
          status = 'accepted_unpaid';
        }
      } else if (booking.statutDisponibilite === 'non_disponible') {
        // Vérifier si c'est annulé ou refusé
        if (booking.statutReservation === 'annulée' || booking.status === 'CANCELLED') {
          status = 'cancelled';
        } else {
          status = 'rejected';
        }
      }

      return {
        id: String(booking.id),
        packageId: String(booking.packageId),
        packageTitle: booking.package?.title || '',
        agencyId: booking.package?.agency?.id || null, // L'agence destinataire (qui possède le package)
        agencyName: booking.package?.agency?.name || '',
        fromAgencyId: booking.client?.agencyId ? String(booking.client.agencyId) : String(finalAgencyId), // Mon agence (émettrice)
        fromAgencyName: booking.client?.agency?.name || '',
        userId: String(booking.clientId),
        status: status,
        statutDisponibilite: booking.statutDisponibilite || 'attente',
        paiement: booking.paiement || 'non_paye',
        montantAvance: booking.montantAvance || 0,
        typeReservation: String(booking.typeReservation || 'partenaire'),
        travelerCount: booking.travelerCount,
        totalPrice: Number(booking.totalPrice) || 0,
        commissions: {
          platform: platformCommission,
          agency: agencyCommission,
          partner: partnerCommission,
        },
        contactInfo: {
          firstName: booking.nom || booking.client?.firstName || '',
          lastName: booking.prenom || booking.client?.lastName || '',
          email: booking.mail || booking.client?.email || '',
          phone: booking.telephone || booking.client?.phone || '',
        },
        createdAt: booking.createdAt,
        updatedAt: booking.updatedAt,
      };
    });
  }, [sentBookingsData, finalAgencyId]);

  // Combiner les données réelles pour tous les onglets
  const allBookings = useMemo(() => {
    if (bookingTab === 'received') {
      return receivedBookings;
    }
    if (bookingTab === 'sent') {
      return sentBookings;
    }
    return [];
  }, [bookingTab, receivedBookings, sentBookings]);

  // Filtrage selon l'onglet
  const filteredBookings = useMemo(() => {
    console.log('14. Filtrage - allBookings:', allBookings.length);
    console.log('15. Filtrage - bookingTab:', bookingTab);
    console.log('16. Filtrage - statusFilter:', statusFilter);
    console.log('17. Filtrage - finalAgencyId:', finalAgencyId);
    
    const filtered = allBookings.filter((booking: any) => {
    const agencyId = String(booking.agencyId);
    const fromAgencyId = String(booking.fromAgencyId);
      const userAgencyIdStr = String(finalAgencyId || 1);
      
      console.log('18. Vérification réservation ID:', booking.id, {
        agencyId,
        fromAgencyId,
        userAgencyIdStr,
        typeReservation: booking.typeReservation,
        status: booking.status,
        bookingTab,
      });
      
    if (bookingTab === 'received') {
        // Pour "reçues", on veut toutes les réservations où le package appartient à mon agence
        // et où typeReservation = 'partenaire' (réservations venant d'autres agences)
        // Inclure toutes les réservations (en attente, acceptées, refusées)
        const match = agencyId === userAgencyIdStr && booking.typeReservation === 'partenaire' && (statusFilter === 'all' || booking.status === statusFilter);
        console.log('19. received - match:', match);
        return match;
    }
    if (bookingTab === 'sent') {
        // Pour "envoyées", on veut toutes les réservations où le client appartient à mon agence
        // et où le package appartient à une autre agence
        // Toutes les réservations sont déjà filtrées par la query, on filtre juste par statut
        const match = statusFilter === 'all' || booking.status === statusFilter;
        console.log('20. sent - match:', match);
        return match;
    }
    return false;
  });

    console.log('23. Réservations filtrées:', filtered.length);
    console.log('24. Détails des réservations filtrées:', filtered);
    
    return filtered;
  }, [allBookings, bookingTab, statusFilter, finalAgencyId]);

  const totals = useMemo(() => {
    return filteredBookings.reduce((acc: any, booking: any) => {
      const totalPrice = Number(booking.totalPrice) || 0;
      let commission = 0;
      let commissionReceived = 0;
      const platformCommission = Number(booking.commissions?.platform) || 0;
      
      if (bookingTab === 'received') {
        // Pour "received" : réservations reçues de partenaires
        // Commissions versées = commission partenaire versée aux agences qui ont réservé
        commission = Number(booking.commissions?.partner) || 0;
        // Montant total = montant total des réservations reçues (sans commission)
        // Commissions reçues = 0 (pas de commissions reçues dans cet onglet)
      } else if (bookingTab === 'sent') {
        // Pour "sent" : réservations envoyées à des partenaires
        // Commissions reçues = commission partenaire que je reçois de l'agence destinataire
        commissionReceived = Number(booking.commissions?.partner) || 0;
        // Montant total = totalPrice + commissions reçues (montant + commission reçue)
        // Commissions versées = 0 (pas de commissions versées dans cet onglet)
      }
      
      // Montant total = Montant de la réservation + Commissions reçues
      const bookingTotal = totalPrice + commissionReceived;
      
      return {
    totalBookings: acc.totalBookings + 1,
        totalRevenue: acc.totalRevenue + bookingTotal,
        totalCommission: acc.totalCommission + commission,
        totalCommissionReceived: (acc.totalCommissionReceived || 0) + commissionReceived,
        totalPlatformCommission: (acc.totalPlatformCommission || 0) + platformCommission,
      };
    }, {
    totalBookings: 0,
    totalRevenue: 0,
    totalCommission: 0,
      totalCommissionReceived: 0,
      totalPlatformCommission: 0,
  });
  }, [filteredBookings, bookingTab]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Réservations</h1>
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              <button
                onClick={() => setBookingTab('received')}
                className={`flex items-center px-4 py-2 ${
                  bookingTab === 'received'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ArrowDownLeft className="w-5 h-5 mr-2" />
                Reçues
              </button>
              <button
                onClick={() => setBookingTab('sent')}
                className={`flex items-center px-4 py-2 ${
                  bookingTab === 'sent'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ArrowUpRight className="w-5 h-5 mr-2" />
                Envoyées
              </button>
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">Tous les statuts</option>
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {false && (
              <button
                onClick={() => navigate('/admin/bookings/new')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nouvelle réservation
              </button>
            )}
          </div>
        </div>

        {/* Résumé des commissions */}
        <div className={`grid gap-6 ${bookingTab === 'received' ? 'grid-cols-4' : 'grid-cols-2'}`}>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Total réservations
            </h3>
            <p className="text-2xl font-semibold">
              {totals.totalBookings}
            </p>
          </div>
          {bookingTab === 'received' && (
            <>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Montant total
            </h3>
            <p className="text-2xl font-semibold">
                  {(Number(totals.totalRevenue) || 0).toLocaleString('fr-FR')} DA
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Commissions versées
              </h3>
              <p className="text-2xl font-semibold">
                  {(Number(totals.totalCommission) || 0).toLocaleString('fr-FR')} DA
              </p>
                <p className="text-xs text-gray-400 mt-1">Versées aux partenaires</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Commission plateforme
            </h3>
            <p className="text-2xl font-semibold">
                  {(Number(totals.totalPlatformCommission) || 0).toLocaleString('fr-FR')} DA
            </p>
          </div>
            </>
          )}
          {bookingTab === 'sent' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-1">
                Commissions reçues
              </h3>
              <p className="text-2xl font-semibold">
                {(Number(totals.totalCommissionReceived) || 0).toLocaleString('fr-FR')} DA
              </p>
              <p className="text-xs text-gray-400 mt-1">Reçues des partenaires</p>
            </div>
          )}
        </div>
        
        {/* Montant net pour l'onglet "Reçues" */}
        {bookingTab === 'received' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Montant net
            </h3>
            <p className="text-2xl font-semibold">
              {((Number(totals.totalRevenue) || 0) - (Number(totals.totalCommission) || 0) - (Number(totals.totalPlatformCommission) || 0)).toLocaleString('fr-FR')} DA
            </p>
            <p className="text-xs text-gray-400 mt-1">Total - Versées - Commission plateforme</p>
          </div>
        )}

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {(receivedBookingsError && bookingTab === 'received') || (sentBookingsError && bookingTab === 'sent') ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
              <p className="text-red-800 font-semibold">Erreur lors du chargement des réservations</p>
              <p className="text-red-600 text-sm mt-1">
                {bookingTab === 'received' ? receivedBookingsError?.message : sentBookingsError?.message}
              </p>
              <button
                onClick={() => {
                  if (bookingTab === 'received') refetchReceivedBookings();
                  else refetchSentBookings();
                }}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Réessayer
              </button>
            </div>
          ) : null}
          {((receivedBookingsLoading && bookingTab === 'received') || (sentBookingsLoading && bookingTab === 'sent')) ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">Chargement des réservations...</p>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-max divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qui a réservé</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package (Créateur)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {bookingTab === 'received' ? 'Montant total' : bookingTab === 'sent' ? 'Commission reçue' : 'Montant'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                        {bookingTab === 'received' ? (
                          <div>
                            <p className="mb-2">Aucune réservation trouvée</p>
                            <p className="text-xs text-gray-400">
                              finalAgencyId: {finalAgencyId || 'non défini'} | 
                              Réservations filtrées: {filteredBookings.length}
                            </p>
                          </div>
                        ) : (
                          'Aucune réservation trouvée'
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((booking: any) => {
                  return (
                    <tr key={booking.id}>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{booking.id}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          // Déterminer qui a fait la réservation
                          const fromAgencyIdStr = booking.fromAgencyId ? String(booking.fromAgencyId) : null;
                          const userAgencyIdStr = String(finalAgencyId || '');
                          
                          // Si c'est mon agence qui a fait la réservation
                          if (fromAgencyIdStr && fromAgencyIdStr === userAgencyIdStr) {
                            return <div className="font-medium text-blue-600">Moi</div>;
                          }
                          
                          // Si c'est une agence partenaire
                          if (booking.fromAgencyName && booking.typeReservation === 'partenaire') {
                            return <div className="font-medium text-gray-900">{booking.fromAgencyName}</div>;
                          }
                          
                          // Si c'est un client direct (en ligne)
                          if (booking.typeReservation === 'enligne' || !booking.fromAgencyName) {
                            return <div className="font-medium text-gray-900">Client direct</div>;
                          }
                          
                          return <div className="font-medium text-gray-900">-</div>;
                        })()}
                          </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="font-medium text-gray-900">{booking.packageTitle}</div>
                        <div className="text-xs text-gray-500 mt-1">Créé par: {booking.agencyName || 'N/A'}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{booking.contactInfo.firstName} {booking.contactInfo.lastName}</div>
                        <div className="text-xs text-gray-500">{booking.contactInfo.email}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap"><BookingStatusBadge status={booking.status} /></td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {bookingTab === 'sent' 
                          ? (Number(booking.commissions?.partner) || 0).toLocaleString('fr-FR') + ' DA'
                          : (Number(booking.totalPrice) || 0).toLocaleString('fr-FR') + ' DA'
                        }
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(() => {
                              try {
                            if (!booking.createdAt) return 'N/A';
                            let date: Date;
                            if (typeof booking.createdAt === 'string') {
                              date = new Date(booking.createdAt);
                                if (isNaN(date.getTime())) {
                                const timestamp = parseInt(booking.createdAt);
                                if (!isNaN(timestamp)) {
                                  date = new Date(timestamp);
                                } else {
                                  date = new Date(booking.createdAt.replace(' ', 'T'));
                                }
                              }
                            } else if (booking.createdAt instanceof Date) {
                              date = booking.createdAt;
                            } else {
                              return 'N/A';
                            }
                            if (isNaN(date.getTime())) {
                              return typeof booking.createdAt === 'string' 
                                ? booking.createdAt.split('T')[0] 
                                : 'N/A';
                                }
                                return format(date, 'dd MMM yyyy', { locale: fr });
                              } catch (error) {
                            if (typeof booking.createdAt === 'string') {
                              const parts = booking.createdAt.split('T')[0].split('-');
                              if (parts.length === 3) {
                                return `${parts[2]}/${parts[1]}/${parts[0]}`;
                              }
                            }
                                return booking.createdAt || 'N/A';
                              }
                            })()}
                          </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {(booking.statutDisponibilite === 'attente' || booking.status === 'pending_availability') && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    await acceptBooking({ variables: { id: parseInt(booking.id) } });
                                    alert('Réservation acceptée avec succès');
                                  } catch (error: any) {
                                    alert('Erreur lors de l\'acceptation : ' + error.message);
                                  }
                                }}
                                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                title="Accepter"
                              >
                                Accepter
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    await requestModifications({ variables: { id: parseInt(booking.id) } });
                                    alert('Demande de modifications envoyée');
                                  } catch (error: any) {
                                    alert('Erreur lors de la demande de modifications : ' + error.message);
                                  }
                                }}
                                className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                                title="Demande de modifications"
                              >
                                Modifier
                              </button>
                              <button
                                onClick={async () => {
                                  if (confirm('Êtes-vous sûr de vouloir refuser cette réservation ?')) {
                                    try {
                                      await rejectBooking({ variables: { id: parseInt(booking.id) } });
                                      alert('Réservation refusée');
                                    } catch (error: any) {
                                      alert('Erreur lors du refus : ' + error.message);
                                    }
                                  }
                                }}
                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                title="Refuser"
                              >
                                Refuser
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setIsDetailsModalOpen(true);
                            }}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Voir les détails"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                    })
                  )}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Modal de détails */}
        {isDetailsModalOpen && selectedBooking && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setIsDetailsModalOpen(false)}
              />
              <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Détails de la réservation #{selectedBooking.id}
                    </h3>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => setIsDetailsModalOpen(false)}
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 mt-4">
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Informations générales</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Référence:</span> #{selectedBooking.id}</div>
                        <div><span className="font-medium">Package:</span> {selectedBooking.packageTitle}</div>
                        <div><span className="font-medium">Statut:</span> <BookingStatusBadge status={selectedBooking.status} /></div>
                        <div><span className="font-medium">Statut disponibilité:</span> {
                          selectedBooking.statutDisponibilite === 'disponible' ? 'Disponible' :
                          selectedBooking.statutDisponibilite === 'attente' ? 'En attente' :
                          'Non disponible'
                        }</div>
                        <div><span className="font-medium">Type réservation:</span> {
                          (() => {
                            const typeValue = String(selectedBooking.typeReservation || '');
                            const option = TYPE_RESERVATION_OPTIONS.find(opt => opt.value === typeValue);
                            return option?.label || typeValue;
                          })()
                        }</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Agence</h4>
                      <div className="space-y-2 text-sm">
                        {bookingTab === 'received' ? (
                          <>
                            <div><span className="font-medium">Agence émettrice:</span> {selectedBooking.fromAgencyName || 'N/A'}</div>
                            <div><span className="font-medium">Agence destinataire:</span> {selectedBooking.agencyName || 'Mon agence'}</div>
                          </>
                        ) : bookingTab === 'sent' ? (
                          <>
                            <div><span className="font-medium">Agence émettrice:</span> {selectedBooking.fromAgencyName || 'Mon agence'}</div>
                            <div><span className="font-medium">Agence destinataire:</span> {selectedBooking.agencyName || 'N/A'}</div>
                          </>
                        ) : null}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Client</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Nom:</span> {selectedBooking.contactInfo.firstName} {selectedBooking.contactInfo.lastName}</div>
                        <div><span className="font-medium">Email:</span> {selectedBooking.contactInfo.email}</div>
                        <div><span className="font-medium">Téléphone:</span> {selectedBooking.contactInfo.phone}</div>
                        <div><span className="font-medium">Nombre de voyageurs:</span> {selectedBooking.travelerCount}</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Paiement</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Statut paiement:</span> {
                          PAIEMENT_OPTIONS.find(opt => opt.value === selectedBooking.paiement)?.label || selectedBooking.paiement
                        }</div>
                        <div><span className="font-medium">Montant avance:</span> {
                          selectedBooking.montantAvance ? `${selectedBooking.montantAvance.toLocaleString('fr-FR')} DA` : '-'
                        }</div>
                        <div><span className="font-medium">Montant total:</span> {
                          (Number(selectedBooking.totalPrice) || 0).toLocaleString('fr-FR')
                        } DA</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Commissions</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Commission plateforme:</span> {
                          (Number(selectedBooking.commissions?.platform) || 0).toLocaleString('fr-FR')
                        } DA</div>
                        <div><span className="font-medium">Commission partenaire:</span> {
                          (Number(selectedBooking.commissions?.partner) || 0).toLocaleString('fr-FR')
                        } DA</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Dates</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Date de création:</span> {
                          (() => {
                            try {
                              if (!selectedBooking.createdAt) return 'N/A';
                              let date: Date;
                              if (typeof selectedBooking.createdAt === 'string') {
                                date = new Date(selectedBooking.createdAt);
                                if (isNaN(date.getTime())) {
                                  const timestamp = parseInt(selectedBooking.createdAt);
                                  if (!isNaN(timestamp)) {
                                    date = new Date(timestamp);
                                  } else {
                                    date = new Date(selectedBooking.createdAt.replace(' ', 'T'));
                                  }
                                }
                              } else if (selectedBooking.createdAt instanceof Date) {
                                date = selectedBooking.createdAt;
                              } else {
                                return 'N/A';
                              }
                              if (isNaN(date.getTime())) {
                                return typeof selectedBooking.createdAt === 'string' 
                                  ? selectedBooking.createdAt.split('T')[0] 
                                  : 'N/A';
                              }
                              return format(date, 'dd MMM yyyy', { locale: fr });
                            } catch {
                              return 'N/A';
                            }
                          })()
                        }</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex gap-2 justify-end">
                    {(selectedBooking.statutDisponibilite === 'attente' || selectedBooking.status === 'pending_availability') && (
                            <>
                            <button
                              onClick={async () => {
                                  try {
                              await acceptBooking({ variables: { id: parseInt(selectedBooking.id) } });
                                    alert('Réservation acceptée avec succès');
                              setIsDetailsModalOpen(false);
                                  } catch (error: any) {
                                    alert('Erreur lors de l\'acceptation : ' + error.message);
                                  }
                                }}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                Accepter
                              </button>
                              <button
                                onClick={async () => {
                                    try {
                              await requestModifications({ variables: { id: parseInt(selectedBooking.id) } });
                                    alert('Demande de modifications envoyée');
                              setIsDetailsModalOpen(false);
                                    } catch (error: any) {
                                    alert('Erreur lors de la demande de modifications : ' + error.message);
                                  }
                                }}
                          className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                              >
                                Demande de modifications
                            </button>
                            <button
                              onClick={async () => {
                                  if (confirm('Êtes-vous sûr de vouloir refuser cette réservation ?')) {
                                  try {
                                await rejectBooking({ variables: { id: parseInt(selectedBooking.id) } });
                                      alert('Réservation refusée');
                                setIsDetailsModalOpen(false);
                                  } catch (error: any) {
                                      alert('Erreur lors du refus : ' + error.message);
                                  }
                                }
                              }}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Refuser
                            </button>
                            </>
                          )}
                          <button
                            onClick={() => {
                        navigate(`/admin/bookings/edit/${selectedBooking.id}`);
                            }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={async () => {
                        if (confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) {
                                try {
                            await deleteBooking({ variables: { id: parseInt(selectedBooking.id) } });
                                  alert('Réservation supprimée avec succès');
                            setIsDetailsModalOpen(false);
                                } catch (error: any) {
                                  alert('Erreur lors de la suppression : ' + error.message);
                                }
                              }
                            }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Supprimer
                          </button>
                        </div>
          </div>
        </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}